import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class FtcDocumentationService {

  constructor(private http: HttpClient) { }

  /**
   * Check Lock Status
   * Action Code: 'n10cpv'
   */
  checkLockStatus(
    baseUrl: string,
    scenario: string,
    jcdKey: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'n10cpv';
    params.p_scenario = scenario;
    params.p_jcd_key = jcdKey;
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Distinct LEIDs Data
   * Action Code: 'n37ya4'
   */
  getDistinctLEIDsData(
    baseUrl: string,
    taxYear: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'n37ya4';
    params.filing_key = '0';
    params.tax_year = taxYear;
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Distinct ME Data
   * Action Code: 'km8ief'
   */
  getDistinctMEData(
    baseUrl: string,
    taxYear: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'km8ief';
    params.filing_key = '0';
    params.tax_year = taxYear;
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Distinct Home Office Data
   * Action Code: 'a91oun'
   */
  getDistinctHomeOfficeData(
    baseUrl: string,
    taxYear: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'a91oun';
    params.filing_key = '0';
    params.tax_year = taxYear;
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Parent ME Dropdown
   * Action Code: 'hmkia6'
   */
  getParentMeDropdown(
    baseUrl: string,
    ssoId: string,
    taxYear: string,
    scenario: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'hmkia6';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Filing Group Dropdown
   * Action Code: 'gk7crv'
   */
  getFilingGroupDropdown(
    baseUrl: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'gk7crv';
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Document Level Code Master
   * Action Code: 'hn5awf'
   */
  getDocLevelCodeMaster(
    baseUrl: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'hn5awf';
    
    // Add required configuration parameters
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || 'N';

    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Save Documentation Data
   * Action Code: '6s61vm'
   * Follows exact AngularJS pattern - settings object already contains tax_year, scenario, jcd_key
   * Note: clob_data removed, only clob_settings sent with scenarioCode and is_issue_key_enabled included
   */
  saveData(
    baseUrl: string,
    settings: any,
    data: any[],
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=6s61vm`;
    
    // Add required configuration parameters to settings before stringifying
    settings.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || settings?.scenarioCode || 'RAF';
    settings.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || settings?.is_issue_key_enabled || 'N';
    
    const jsonSettings = JSON.stringify(settings);

    // Build params exactly like AngularJS DocumentationFactory.saveData:
    // IMPORTANT: Settings values (tax_year, scenario, jcd_key, sso_id, scenarioCode, is_issue_key_enabled) must ALWAYS be preserved
    // because data[0] might have null/undefined/empty values or different values that would overwrite them
    
    // Store critical values from settings FIRST (these are the source of truth)
    const preservedTaxYear = settings?.tax_year;
    const preservedScenario = settings?.scenario;
    const preservedJcdKey = settings?.jcd_key;
    const preservedSsoId = settings?.sso_id;
    const preservedScreenKey = settings?.screen_key;
    const preservedObjectId = settings?.object_id;
    
    let params: any = {};
    params = _.merge({}, settings, params);       // Settings contains tax_year, scenario, jcd_key, sso_id
    params = _.merge({}, data[0], params);        // Data object (may overwrite with null/undefined!)
    
    // CRITICAL: ALWAYS restore preserved values from settings (they are the source of truth)
    // This ensures settings values are NEVER lost, even if data[0] tries to overwrite them
    if (preservedTaxYear !== null && preservedTaxYear !== undefined && preservedTaxYear !== '') {
      params.tax_year = preservedTaxYear;
    }
    if (preservedScenario !== null && preservedScenario !== undefined && preservedScenario !== '') {
      params.scenario = preservedScenario;
    }
    if (preservedJcdKey !== null && preservedJcdKey !== undefined && preservedJcdKey !== '') {
      params.jcd_key = preservedJcdKey;
    }
    if (preservedSsoId !== null && preservedSsoId !== undefined && preservedSsoId !== '') {
      params.sso_id = preservedSsoId;
    }
    if (preservedScreenKey !== null && preservedScreenKey !== undefined && preservedScreenKey !== '') {
      params.screen_key = preservedScreenKey;
    }
    if (preservedObjectId !== null && preservedObjectId !== undefined && preservedObjectId !== '') {
      params.object_id = preservedObjectId;
    }
    
    // Add clob_settings (clob_data removed as requested)
    params = _.extend({ clob_settings: jsonSettings }, params);
    
    // Add required configuration parameters (must be added AFTER clob_settings)
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalParams?.scenarioCode || globalParams?.scenario_code || settings?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || globalParams?.globalParams?.is_issue_key_enabled || settings?.is_issue_key_enabled || 'N';
    
    // Final conversion to numbers - ensure they are always numbers (not strings)
    // Only convert if value exists and is valid
    const toNumber = (value: any): number | null => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const num = Number(value);
      return isNaN(num) ? null : num;
    };
    
    // Convert to numbers (preserve null if truly missing for validation)
    if (params.tax_year !== null && params.tax_year !== undefined && params.tax_year !== '') {
      const num = toNumber(params.tax_year);
      if (num !== null) {
        params.tax_year = num;
      }
    } else {
      // Try fallback
      const taxYear = filterParams?.tax_year || globalParams?.tax_year;
      const num = toNumber(taxYear);
      if (num !== null) {
        params.tax_year = num;
      }
    }
    
    if (params.scenario !== null && params.scenario !== undefined && params.scenario !== '') {
      const num = toNumber(params.scenario);
      if (num !== null) {
        params.scenario = num;
      }
    } else {
      const scenario = filterParams?.scenario || globalParams?.scenario;
      const num = toNumber(scenario);
      if (num !== null) {
        params.scenario = num;
      }
    }
    
    if (params.jcd_key !== null && params.jcd_key !== undefined && params.jcd_key !== '') {
      const num = toNumber(params.jcd_key);
      if (num !== null) {
        params.jcd_key = num;
      }
    } else {
      const jcdKey = filterParams?.jcd_key || globalParams?.jcd_key;
      const num = toNumber(jcdKey);
      if (num !== null) {
        params.jcd_key = num;
      }
    }
    
    // Final validation - check if values are missing
    if (!params.tax_year || params.tax_year === null || params.tax_year === undefined) {
      console.error('FTC Documentation saveData: tax_year is MISSING after all processing:', {
        preservedTaxYear,
        fromSettings: settings?.tax_year,
        fromData: data[0]?.tax_year,
        finalValue: params.tax_year,
        filterParams: filterParams?.tax_year,
        globalParams: globalParams?.tax_year
      });
    }
    if (!params.scenario || params.scenario === null || params.scenario === undefined) {
      console.error('FTC Documentation saveData: scenario is MISSING after all processing:', {
        preservedScenario,
        fromSettings: settings?.scenario,
        fromData: data[0]?.scenario,
        finalValue: params.scenario,
        filterParams: filterParams?.scenario,
        globalParams: globalParams?.scenario
      });
    }
    if (!params.jcd_key || params.jcd_key === null || params.jcd_key === undefined) {
      console.error('FTC Documentation saveData: jcd_key is MISSING after all processing:', {
        preservedJcdKey,
        fromSettings: settings?.jcd_key,
        fromData: data[0]?.jcd_key,
        finalValue: params.jcd_key,
        filterParams: filterParams?.jcd_key,
        globalParams: globalParams?.jcd_key
      });
    }

    console.log('FTC Documentation saveData - Input settings (with scenarioCode and is_issue_key_enabled):', settings);
    console.log('FTC Documentation saveData - Input data[0]:', data[0]);
    console.log('FTC Documentation saveData - Final params (BEFORE HTTP POST):', {
      sso_id: params.sso_id,
      tax_year: params.tax_year,
      scenario: params.scenario,
      jcd_key: params.jcd_key,
      operation_type: params.operation_type,
      level_key: params.level_key,
      value_key: params.value_key,
      document_key: params.document_key,
      screen_key: params.screen_key,
      object_id: params.object_id,
      scenarioCode: params.scenarioCode,
      is_issue_key_enabled: params.is_issue_key_enabled,
      types: {
        tax_year: typeof params.tax_year,
        scenario: typeof params.scenario,
        jcd_key: typeof params.jcd_key,
        sso_id: typeof params.sso_id
      },
      hasClobSettings: !!params.clob_settings
    });
    console.log('FTC Documentation saveData - Full params object:', JSON.stringify(params, null, 2));

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(url, params, { headers, withCredentials: true });
  }

  /**
   * Private helper method to make HTTP requests
   * Uses GET request like JsonObjectFactoryService
   */
  private _makeLoadRequest(baseUrl: string, params: any): Observable<any> {
    // Clean up params - remove undefined/null/empty values
    const cleanParams: any = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        cleanParams[key] = params[key].toString();
      }
    });

    const url = `${baseUrl}/loadJsonObject`;

    console.log('FTC Documentation Service: Making GET request to:', url, 'with params:', cleanParams);

    // Use GET request like JsonObjectFactoryService does
    return this.http.get<any>(url, { 
      params: cleanParams,
      withCredentials: true 
    });
  }
}
